import { CalendarDay, CalendarDayValue } from 'calendar-blocks';
import moment from 'moment';

import { cn } from '@/lib/utils';
import useDatePickerContext from '@/hooks/useDatePickerContext';

type CalendarValue = CalendarDayValue & {
  key: string;
};

type Props = {
  value: CalendarValue;
  monthLastDay: CalendarValue;
  lastItem: CalendarValue;
  rangeValue: {
    start: Date | null;
    end: Date | null;
  };
  isOneDate?: boolean;
  onRangeValueChange: (value: { start: Date | null; end: Date | null }) => void;
};

export default function Day({
  value,
  monthLastDay,
  lastItem,
  rangeValue,
  isOneDate,
  onRangeValueChange,
}: Props) {
  const {
    handleCalendarErrorType,
    calendarHasErrorType,
    clearCalendarErrorType,
  } = useDatePickerContext();

  const isSingleDate = checkForSingleDate(value.date.getTime());

  function renderButtonDate(date: Date) {
    const dayOfMonth = date.getDate();

    if (dayOfMonth < 10) {
      return `0${dayOfMonth}`;
    }
    return dayOfMonth;
  }

  function checkForSingleDate(dateTime: number) {
    const startDate = moment(rangeValue.start).valueOf();
    const endDate = moment(rangeValue.end).valueOf();

    return dateTime === startDate && dateTime === endDate;
  }

  return (
    <div
      key={value.key}
      className={cn(
        'h-[44px] leading-[44px] flex-[1_0_14%] m-0 p-0 [&]:border-l [&:nth-child(7n)]:border-r',
        {
          invisible: value.isDifferentMonth,
          'border-t': value.date.getDate() <= 28 && !value.isDifferentMonth,
          'border-b': value.date.getDate() > 21 && !value.isDifferentMonth,
          'border-r': value == monthLastDay,
          'flex-[1_0_14.5%]': value == monthLastDay && value != lastItem,
        },
      )}>
      <CalendarDay
        onClick={(event, date, disabled) => {
          if (disabled) {
            return handleCalendarErrorType('datesNoLongerAvailable');
          } else {
            if (calendarHasErrorType('datesNoLongerAvailable')) {
              clearCalendarErrorType('datesNoLongerAvailable');
            }
          }

          const target = event.target as HTMLButtonElement;
          const buttonIsDisabled = target.getAttribute('data-disabled');

          if (!buttonIsDisabled) {
            if (isOneDate) {
              return onRangeValueChange({
                start: value.date,
                end: value.date,
              });
            }

            if (rangeValue.start == null) {
              onRangeValueChange({
                start: value.date,
                end: rangeValue.end,
              });
            }
            if (rangeValue.start && rangeValue.end) {
              onRangeValueChange({ start: value.date, end: null });
            }
          }
        }}
        className={cn(
          'block border-gray-200 w-full text-center relative select-none',
          {
            'is-single-date': isSingleDate,
          },
        )}
        value={value}
        key={value.key}>
        {renderButtonDate(value.date)}
      </CalendarDay>
    </div>
  );
}
